import { useCallback, useEffect, useState } from 'react';
import { pcommon, pcontract } from '@/proto/proto';
import api from '@/api';
import { Button, message, PageHeader, Spin, Table, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { ErrorMessage } from '@/constants/message';
import { FORMAT_DATE_EXCEPT_SECOND_KR, FORMAT_DATE_FORMAL_KR, PATH_CONTRACT, PATH_CONTRACT_IMPORT, PATH_CONTRACT_WRITE } from '@/constants/constant';
import { useSelector } from 'react-redux';
import { RootState } from '@/rootReducer';

const ContractListPage = () => {
  const [contracts, setContracts] = useState<pcontract.IPbContractList>();
  const { current: company } = useSelector((state: RootState) => state.company);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();
  const [pagination, setPagination] = useState<pcommon.IPbPageable>({
    total: 0,
    page: 1,
    current: 1,
    size: 10,
  });

  const getContracts = useCallback(
    (companyId) => {
      setLoading(true);
      api
        .get(PATH_CONTRACT, {
          params: { companyId, ...pagination },
        })
        .then(({ data }) => {
          setContracts(data);
        })
        .catch((e) => {
          setError(e?.response?.data?.message ?? ErrorMessage.network.disconnected);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [pagination],
  );

  useEffect(() => {
    if (company) {
      getContracts(company.base?.id);
    }
  }, [company, getContracts, pagination]);

  useEffect(() => {
    if (error) {
      message.error(error);
      setError(null);
    }
  }, [error]);

  return (
    <PageHeader
      title="계약 목록"
      subTitle="전체 계약 목록"
      extra={[
        <Button type="primary">
          <Link to={PATH_CONTRACT_WRITE}>등록</Link>
        </Button>,
        <Button>
          <Link to={PATH_CONTRACT_IMPORT}>불러오기</Link>
        </Button>,
      ]}
      style={{ background: '#fff', border: '1px solid rgb(235, 237, 240)' }}
    >
      <Spin spinning={loading}>
        <Table
          dataSource={contracts?.contract ?? []}
          pagination={{
            total: Number(contracts?.pageable?.total),
            onShowSizeChange: (page, size) => {
              setPagination({ ...pagination, page: page - 1, size });
            },
            onChange: (page) => {
              setPagination({ ...pagination, page: page - 1 });
            },
          }}
        >
          <Table.Column title="차량번호" dataIndex="plateNo" key="v" />
          <Table.Column title="고객" dataIndex="user" key="userName" render={(user) => <Typography.Text>{user.first()?.name}</Typography.Text>} />
          <Table.Column
            title="주소"
            key="userAddr"
            render={(text, record: pcontract.IPbContract) => <Typography.Text>{record.user ? record.user.first()?.addr1 : ''}</Typography.Text>}
          />
          <Table.Column title="상태" dataIndex="status" key="status" render={() => <Tag>READY</Tag>} />
          <Table.Column
            title="등록일"
            dataIndex="base"
            key="createdAt"
            sorter={(a: pcontract.IPbContract, b: pcontract.IPbContract) => moment(a.base?.createdAt).unix() - moment(b.base?.createdAt).unix()}
            defaultSortOrder="descend"
            sortDirections={['descend', 'ascend']}
            render={(base: pcommon.IPbBase) => {
              const createAt = moment(base.createdAt);
              const format = createAt.year() === moment().year() ? FORMAT_DATE_EXCEPT_SECOND_KR : FORMAT_DATE_FORMAL_KR;
              return createAt.format(format);
            }}
          />
        </Table>
      </Spin>
    </PageHeader>
  );
};

export default ContractListPage;
